import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { LeaderboardData, Participant } from '../interface/HallOfFame';
import HallOfFameService from '../Api_Module/Api_Services/HallOfFameService';

interface HallOfFameState {
  leaderboardData: LeaderboardData | null;
  loading: boolean;
  error: string | null;
  submissionSuccess: boolean;
  selectedParticipant: Participant | null;
}

const initialState: HallOfFameState = {
  leaderboardData: null,
  loading: false,
  error: null,
  submissionSuccess: false,
  selectedParticipant: null,
};

// Async thunk to fetch leaderboard data
export const fetchLeaderboard = createAsyncThunk(
  'hallOfFame/fetchLeaderboard',
  async (params: { tier?: string; page?: number; size?: number } = {}, { rejectWithValue }) => {
    try {
      const { tier = 'All', page = 0, size = 50 } = params;
      const response = await HallOfFameService.getLeaderboard(tier, page, size);

      // Handle different backend response formats
      let participants = [];

      if (Array.isArray(response)) {
        // Backend returns array directly
        participants = response;
      } else if (response.participants) {
        // Backend returns object with participants array
        participants = response.participants;
      }

      // Transform backend data to match frontend interface
      const transformedParticipants = participants.map((p: any) => ({
        ...p,
        // Map backend field names to frontend interface - NO CALCULATIONS
        totalROI: p.totalReturn !== undefined ? p.totalReturn : (p.totalROI || 0),
        vsAverage: p.vsAverage !== undefined ? p.vsAverage : 0, // Use backend value directly
        averageReturn: p.averageReturn !== undefined ? p.averageReturn : 0, // Use backend value directly
        // For leaderboard data, we don't have detailed stock picks, only topPick
        stockPicks: p.topPick ? [{
          symbol: p.topPick.symbol,
          name: p.topPick.symbol,
          initialPrice: 0, // Not available in leaderboard data
          currentPrice: 0, // Not available in leaderboard data
          roi: p.topPick.returnPercentage, // Map directly from backend, NO calculation
          logoUrl: undefined,
        }] : [],
        currentPortfolioValue: p.currentPortfolioValue ?? p.portfolioValue ?? 10000,
        portfolioValue: p.portfolioValue ?? p.initialPortfolioValue ?? 10000,
        name: p.name || 'Anonymous',
        socialHandle: p.socialHandle || p.handle || '@unknown',
        tier: p.tier || 'F',
        lockInDate: p.lockDate || p.lockedAt || p.submissionDate || new Date().toISOString(),
        submissionDate: p.joinedAt || new Date().toISOString(),
        performanceHistory: Array.isArray(p.performanceHistory) ? p.performanceHistory : [],
      }));

      // Log stock picks availability for debugging
      console.log('📊 Leaderboard loaded with', transformedParticipants.length, 'participants');
      const participantsWithPicks = transformedParticipants.filter(p => p.stockPicks.length > 0);
      console.log('📊 Participants with stock picks:', participantsWithPicks.length);
      if (participantsWithPicks.length > 0) {
        console.log('📊 Sample participant with picks:', participantsWithPicks[0].name, 'has', participantsWithPicks[0].stockPicks.length, 'picks');
      }

      // Use averageReturn from first participant (all have same community average in backend response)
      // NO CALCULATION - just map from backend
      const communityAverageROI = transformedParticipants.length > 0 && transformedParticipants[0].averageReturn !== undefined
        ? transformedParticipants[0].averageReturn
        : 0;

      const sortedByROI = [...transformedParticipants].sort((a: any, b: any) => (b.totalROI || 0) - (a.totalROI || 0));

      // Build complete response structure
      return {
        participants: transformedParticipants,
        communityStats: {
          averageROI: communityAverageROI, // Use backend value directly
          medianROI: transformedParticipants.length > 0 ? (sortedByROI[Math.floor(transformedParticipants.length / 2)]?.totalROI || 0) : 0,
          topPerformerROI: sortedByROI[0]?.totalROI || 0,
          bottomPerformerROI: sortedByROI[sortedByROI.length - 1]?.totalROI || 0,
          totalParticipants: transformedParticipants.length,
        },
        communityAverageHistory: response.communityAverageHistory || [],
        lastUpdated: new Date().toISOString(),
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch leaderboard');
    }
  }
);

// Async thunk to submit participant picks
export const submitParticipantPicks = createAsyncThunk(
  'hallOfFame/submitPicks',
  async (
    data: {
      name: string;
      email: string;
      socialHandle: string;
      tickers: string[];
      prices?: (number | undefined)[];
    },
    { rejectWithValue }
  ) => {
    try {
      // Step 1: Create participant
      const participant = await HallOfFameService.createParticipant({
        name: data.name,
        email: data.email,
        socialHandle: data.socialHandle,
      });

      // Step 2: Submit picks for the participant
      const picks = data.tickers.map((symbol, index) => ({
        symbol,
        entryPrice: data.prices?.[index] // Include current price as entry price
      }));

      console.log('🎯 Submitting picks with prices:', picks);
      const portfolio = await HallOfFameService.submitPicks(participant.id, picks);

      return { participant, portfolio };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to submit picks');
    }
  }
);

// Async thunk to fetch participant details
export const fetchParticipantDetails = createAsyncThunk(
  'hallOfFame/fetchParticipantDetails',
  async (participantId: string, { rejectWithValue }) => {
    try {
      const response = await HallOfFameService.getParticipantDetails(participantId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch participant details');
    }
  }
);

const HallOfFameSlice = createSlice({
  name: 'hallOfFame',
  initialState,
  reducers: {
    clearSubmissionSuccess: (state) => {
      state.submissionSuccess = false;
    },
    setSelectedParticipant: (state, action: PayloadAction<Participant | null>) => {
      state.selectedParticipant = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch leaderboard
      .addCase(fetchLeaderboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeaderboard.fulfilled, (state, action) => {
        state.loading = false;
        state.leaderboardData = action.payload;
      })
      .addCase(fetchLeaderboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Submit picks
      .addCase(submitParticipantPicks.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.submissionSuccess = false;
      })
      .addCase(submitParticipantPicks.fulfilled, (state) => {
        state.loading = false;
        state.submissionSuccess = true;
      })
      .addCase(submitParticipantPicks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.submissionSuccess = false;
      })
      // Fetch participant details
      .addCase(fetchParticipantDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchParticipantDetails.fulfilled, (state, action) => {
        state.loading = false;

        // Transform the API response to match our interface
        const response = action.payload;
        const participant = response.participant;
        const portfolio = response.portfolio;

        // Transform stock picks from portfolio.picks to stockPicks format
        const initialInvestmentPerStock = (portfolio?.initialValue || 10000) / 5;
        const totalCurrentValue = portfolio?.currentValue || portfolio?.initialValue || 10000;

        // Log the raw backend response for debugging
        console.log('🔍 Raw portfolio response from backend:', JSON.stringify(portfolio, null, 2));
        console.log('🔍 Initial investment per stock:', initialInvestmentPerStock);

        const stockPicks = portfolio?.picks?.map((pick: any) => {
          // PRIORITY 1: Check if backend provides investment value directly
          if (pick.investmentValue !== undefined || pick.currentValue !== undefined || pick.value !== undefined) {
            const backendValue = pick.investmentValue || pick.currentValue || pick.value;
            console.log(`🔍 ${pick.symbol}: Using backend-provided value: ${backendValue}`);
            return {
              symbol: pick.symbol,
              name: pick.symbol,
              initialPrice: pick.entryPrice,
              currentPrice: pick.currentPrice,
              roi: pick.returnPercentage, // Map directly from backend, NO calculation
              investmentValue: backendValue,
              logoUrl: undefined,
            };
          }

          // PRIORITY 2: Check if backend provides shares/quantity
          if (pick.shares !== undefined || pick.quantity !== undefined) {
            const shares = pick.shares || pick.quantity;
            const value = shares * pick.currentPrice;
            console.log(`🔍 ${pick.symbol}: Using backend shares: ${shares} × ${pick.currentPrice} = ${value}`);
            return {
              symbol: pick.symbol,
              name: pick.symbol,
              initialPrice: pick.entryPrice,
              currentPrice: pick.currentPrice,
              roi: pick.returnPercentage, // Map directly from backend, NO calculation
              investmentValue: value,
              logoUrl: undefined,
            };
          }

          // PRIORITY 3: Calculate using shares-based approach
          // shares = initialInvestment / entryPrice
          // currentValue = shares × currentPrice
          const shares = pick.entryPrice > 0 ? initialInvestmentPerStock / pick.entryPrice : 0;
          const value = shares * pick.currentPrice;

          console.log(`🔍 ${pick.symbol}:`,
                     `\n  Entry: $${pick.entryPrice}`,
                     `\n  Current: $${pick.currentPrice}`,
                     `\n  Shares: ${shares.toFixed(4)}`,
                     `\n  Value: $${value.toFixed(2)}`,
                     `\n  ROI: ${pick.returnPercentage.toFixed(2)}%`);

          return {
            symbol: pick.symbol,
            name: pick.symbol,
            initialPrice: pick.entryPrice,
            currentPrice: pick.currentPrice,
            roi: pick.returnPercentage, // Map directly from backend, NO calculation
            investmentValue: value,
            logoUrl: undefined,
          };
        }) || [];

        // Create transformed participant object
        const transformedParticipant = {
          ...participant,
          stockPicks: stockPicks,
          portfolioValue: portfolio?.initialValue || 10000,
          currentPortfolioValue: portfolio?.currentValue || portfolio?.initialValue || 10000,
          totalROI: portfolio?.totalReturn || 0,
          lockInDate: portfolio?.lockDate || participant.lockDate || new Date().toISOString(),
          submissionDate: portfolio?.submittedAt || participant.joinedAt || new Date().toISOString(),
          performanceHistory: [], // TODO: Add if performance history endpoint is available
          tier: participant.tier || 'F',
          socialHandle: participant.socialHandle || '@unknown',
        };

        console.log('🔍 Transformed participant details:', transformedParticipant);
        console.log('🔍 Stock picks transformed:', stockPicks);

        state.selectedParticipant = transformedParticipant;
      })
      .addCase(fetchParticipantDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearSubmissionSuccess, setSelectedParticipant, clearError } = HallOfFameSlice.actions;
export default HallOfFameSlice.reducer;

